
import { PageContainer } from "@/components/ui/page-container";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { HeartPulse, Award, Users, Code, Github, Mail, ExternalLink } from "lucide-react";
import { OptimizedImage } from "@/components/ui/optimized-image";

const About = () => {
  return (
    <PageContainer
      title="À Propos"
      variant="default"
      intensity="low"
      className="py-6"
    >
      <div className="max-w-5xl mx-auto">
        <Tabs defaultValue="app" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="app">Notre Application</TabsTrigger>
            <TabsTrigger value="mission">Notre Mission</TabsTrigger>
            <TabsTrigger value="creator">Le Créateur</TabsTrigger>
          </TabsList>

          <TabsContent value="app" className="space-y-6 animate-fade-in">
            <p className="text-lg text-muted-foreground">
              Médicace est une application de santé conçue pour faciliter la gestion et le suivi des soins médicaux.
              Notre plateforme offre une gamme complète d'outils pour améliorer votre expérience de santé.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <Card className="bg-white/5 border border-primary/20 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="rounded-full w-12 h-12 bg-blue-100 flex items-center justify-center mb-4">
                    <HeartPulse className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">Suivi de Santé</h3>
                  <p className="text-muted-foreground text-sm">
                    Suivez vos médicaments, rendez-vous et maintenir un journal pour surveiller votre santé au quotidien.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border border-primary/20 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="rounded-full w-12 h-12 bg-purple-100 flex items-center justify-center mb-4">
                    <Users className="h-6 w-6 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">Pour Tous</h3>
                  <p className="text-muted-foreground text-sm">
                    Des fonctionnalités adaptées aussi bien aux patients qu'aux professionnels de santé.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border border-primary/20 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="rounded-full w-12 h-12 bg-green-100 flex items-center justify-center mb-4">
                    <Award className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">IA Médicale</h3>
                  <p className="text-muted-foreground text-sm">
                    Assistant IA pour répondre à vos questions médicales et vous guider dans vos démarches de santé.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="mt-10">
              <h2 className="text-xl font-bold mb-4">Statistiques</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white/5 border border-primary/20 rounded-lg p-6 text-center backdrop-blur-sm">
                  <p className="text-3xl font-bold text-primary">1200+</p>
                  <p className="text-sm text-muted-foreground">Utilisateurs</p>
                </div>
                <div className="bg-white/5 border border-primary/20 rounded-lg p-6 text-center backdrop-blur-sm">
                  <p className="text-3xl font-bold text-primary">50+</p>
                  <p className="text-sm text-muted-foreground">Professionnels</p>
                </div>
                <div className="bg-white/5 border border-primary/20 rounded-lg p-6 text-center backdrop-blur-sm">
                  <p className="text-3xl font-bold text-primary">5000+</p>
                  <p className="text-sm text-muted-foreground">Rendez-vous</p>
                </div>
                <div className="bg-white/5 border border-primary/20 rounded-lg p-6 text-center backdrop-blur-sm">
                  <p className="text-3xl font-bold text-primary">98%</p>
                  <p className="text-sm text-muted-foreground">Satisfaction</p>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="mission" className="space-y-6 animate-fade-in">
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground">
                Notre mission est de rendre les soins de santé plus accessibles, plus intuitifs et plus personnalisés pour tous.
              </p>

              <div className="bg-white/5 border border-primary/20 rounded-lg p-6 backdrop-blur-sm">
                <h3 className="text-lg font-medium mb-4">Vision</h3>
                <p className="text-muted-foreground">
                  Nous envisageons un monde où chaque personne a accès à des soins de santé de qualité, où la technologie rapproche les patients et les professionnels de santé, et où les barrières traditionnelles aux soins sont éliminées.
                </p>
              </div>

              <div className="bg-white/5 border border-primary/20 rounded-lg p-6 backdrop-blur-sm">
                <h3 className="text-lg font-medium mb-4">Objectifs</h3>
                <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                  <li>Faciliter la gestion des soins personnels</li>
                  <li>Améliorer la communication patient-praticien</li>
                  <li>Offrir un accès à l'information médicale fiable</li>
                  <li>Simplifier la prise de rendez-vous médicaux</li>
                  <li>Fournir des rappels et des suivis personnalisés</li>
                </ul>
              </div>

              <div className="bg-white/5 border border-primary/20 rounded-lg p-6 backdrop-blur-sm">
                <h3 className="text-lg font-medium mb-4">Engagement</h3>
                <p className="text-muted-foreground">
                  Nous nous engageons à développer des outils qui respectent la confidentialité des données, qui sont accessibles à tous, et qui évoluent constamment pour répondre aux besoins changeants de nos utilisateurs.
                </p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="creator" className="animate-fade-in">
            <div className="glass-morphism rounded-xl overflow-hidden">
              <div className="md:flex">
                <div className="md:w-1/3 bg-gradient-to-br from-primary/10 to-primary/5 p-8 flex flex-col items-center justify-center">
                  <div className="w-48 h-48 rounded-full border-4 border-primary/30 overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center mb-6">
                    <OptimizedImage 
                      src="/lovable-uploads/3de2264e-9d3c-4974-a741-4af64d5dbe6b.png" 
                      alt="Carl Enockson Alexis" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h2 className="text-2xl font-bold text-center">Carl Enockson Alexis</h2>
                  <p className="text-muted-foreground text-center mt-2">Entrepreneur</p>
                  
                  <div className="flex gap-4 mt-6">
                    <Button variant="outline" size="icon" asChild>
                      <a href="https://github.com/AlexisCarle-Nockson" target="_blank" rel="noopener noreferrer">
                        <Github className="h-5 w-5" />
                      </a>
                    </Button>
                    <Button variant="outline" size="icon" asChild>
                      <a href="mailto:alexiscarlenockson98@gmail.com">
                        <Mail className="h-5 w-5" />
                      </a>
                    </Button>
                    <Button variant="outline" size="icon" asChild>
                      <a href="https://portfolio-alexis-nockson.vercel.app/" target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-5 w-5" />
                      </a>
                    </Button>
                  </div>
                </div>
                
                <div className="md:w-2/3 p-8">
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold flex items-center gap-2">
                        <Code className="h-5 w-5 text-primary" />
                        À propos du créateur
                      </h3>
                      <p className="mt-3 text-muted-foreground">
                        Carl Enockson Alexis est un entrepreneur passionné par la santé numérique et l'innovation technologique. 
                        Originaire de Cap-Haïtien, Haïti, il a développé Médicace avec la vision d'améliorer l'accessibilité aux soins 
                        de santé grâce à la technologie.
                      </p>
                      <p className="mt-3 text-muted-foreground">
                        Passionné d'innovation et de technologie, Carl a mis son enthousiasme et ses compétences au service de la création
                        de Médicace, combinant sa vision entrepreneuriale avec une profonde compréhension des défis du secteur de la santé.
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-semibold">Inspiration</h3>
                      <p className="mt-3 text-muted-foreground">
                        L'inspiration derrière Médicace est née de l'observation des difficultés d'accès aux services de santé 
                        en Haïti et dans d'autres régions en développement. Carl a vu comment la technologie pouvait combler 
                        ces lacunes et faciliter la communication entre patients et professionnels de santé.
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-semibold">Vision</h3>
                      <p className="mt-3 text-muted-foreground">
                        Sa vision est de continuer à développer Médicace comme une plateforme complète qui répond aux besoins 
                        spécifiques des communautés mal desservies, tout en offrant des fonctionnalités innovantes qui profitent 
                        à tous les utilisateurs, quel que soit leur emplacement ou leur niveau d'accès aux soins de santé traditionnels.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </PageContainer>
  );
};

export default About;
